import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import { AutoGrowTextarea, ErrorBanner, SortTh, lower, rowClassName, sortRows, useGridSort } from './Configuration';
import { displayDate, hours, money, moneyWhole, toDateInput } from './registerFormat';
import { useRegisterDirty } from '../unsavedChangesGuard';
import { DRAWDOWN_VALUE_TYPE } from '../billingCategories';

export const ITEM_SORT_VALUE = {
  lineNumber: (r) => r.lineNumber,
  itemNumber: (r) => lower(r.itemNumber),
  description: (r) => lower(r.description),
  // -Infinity, not 0: a line that hasn't been drawn against yet sorts below a genuine $0 draw rather
  // than tying with it.
  amountUsed: (r) => r.amountUsed ?? -Infinity,
  hoursUsed: (r) => r.hoursUsed ?? -Infinity,
  dasNumber: (r) => lower(r.dasNumber),
  dasSubmittedDate: (r) => r.dasSubmittedDate ?? '',
  billed: (r) => (r.billed ? 1 : 0),
};

// Rounded to 2dp: the API's @IsNumber({ maxDecimalPlaces: 2 }) rejects a third decimal outright, and
// nothing else stops one being pasted into an amount box.
function money2(value) {
  return Math.round(Number(value) * 100) / 100;
}

// `bucket`/`item` are live grid rows (edited in place), not separate drafts -- always PATCHed, since
// a new bucket/line is created with minimal defaults directly (see addBucket/addItem) rather than
// through this payload shape.
function bucketPayload(bucket) {
  return {
    name: bucket.name.trim(),
    startingAmount: money2(bucket.startingAmount || 0),
    startingHours: bucket.startingHours === '' || bucket.startingHours == null ? null : money2(bucket.startingHours),
    hourlyRate: bucket.hourlyRate === '' || bucket.hourlyRate == null ? null : money2(bucket.hourlyRate),
    milestoneId: bucket.milestoneId || null,
    notes: (bucket.notes || '').trim() === '' ? null : bucket.notes.trim(),
  };
}

function itemPayload(item) {
  const textOrClear = (v) => ((v || '').trim() === '' ? null : v.trim());
  const numOrClear = (v) => (v === '' || v == null ? null : money2(v));
  return {
    itemNumber: textOrClear(item.itemNumber),
    description: textOrClear(item.description),
    requestedDate: toDateInput(item.requestedDate) || null,
    approvedDate: toDateInput(item.approvedDate) || null,
    amountUsed: numOrClear(item.amountUsed),
    hoursUsed: numOrClear(item.hoursUsed),
    dasNumber: textOrClear(item.dasNumber),
    dasSubmittedDate: toDateInput(item.dasSubmittedDate) || null,
    notes: textOrClear(item.notes),
    billed: !!item.billed,
  };
}

// The project's Draw-down milestones, for the bucket's milestone picker. Fetched once per project
// rather than per card: every card offers the same list, and the tab already loads the register in
// one call. Only Draw-down ones -- the API rejects anything else (ADR 0046), so offering a
// Committed milestone here would just be a 400 waiting to happen.
function useDrawdownMilestones(projectId, enabled) {
  const [milestones, setMilestones] = useState([]);
  useEffect(() => {
    if (!enabled || !projectId) return;
    let live = true;
    apiFetch(`/milestones?projectId=${projectId}`)
      .then((rows) => { if (live) setMilestones(rows.filter((m) => m.valueType === DRAWDOWN_VALUE_TYPE)); })
      .catch(() => { if (live) setMilestones([]); });
    return () => { live = false; };
  }, [projectId, enabled]);
  return milestones;
}

/**
 * A project's billed-as-used drawdown log -- replaces "<customer> BAU Reports and Interface
 * Budget.xlsx". Customer-visible in full: it *is* the balance they're spending against.
 *
 * A project can have more than one pot (Clark has a 1,160-hour SOW reports bucket and a 104-hour
 * interface bucket), so the page is a list of buckets each with its own lines and running balance.
 * Balances come from the API (BilledAsUsedService.findForProject), never recomputed here -- one
 * implementation, and the arithmetic on a customer-facing document isn't duplicated in two languages.
 *
 * Each bucket is its own editable unit: its header fields and its lines grid edit in place, with one
 * Add-line/Save toolbar at the top of the bucket's card. General notes stay staff-only regardless of
 * `readOnly` (the API's note routes carry no `@AllowExternal`), so that panel is keyed off `portal`.
 *
 * One tab of ProjectRegisters.js, which owns the page header and the project picker. Also rendered
 * by CustomerProjectRegisters.js for a customer contact with edit rights on this project (item 5) --
 * `readOnly` collapses the "+ Add budget" control and every bucket/line write affordance.
 */
export default function BilledAsUsedRegister({ projectId, onCoverageChange, readOnly = false, portal = false }) {
  const [data, setData] = useState(null); // { buckets, notes, totals } | null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [dirtyBucketIds, setDirtyBucketIds] = useState(() => new Set());
  const [dirtyItemIds, setDirtyItemIds] = useState(() => new Set());
  const [busy, setBusy] = useState('');

  useRegisterDirty(dirtyBucketIds.size > 0 || dirtyItemIds.size > 0);
  // Internal-only, so not fetched in the portal at all: /milestones carries no @AllowExternal, and a
  // canEdit customer must not be able to repoint which contract line their pot draws against.
  const drawdownMilestones = useDrawdownMilestones(projectId, !portal && !readOnly);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    apiFetch(`/billed-as-used?projectId=${projectId}`)
      .then((body) => {
        setData(body);
        onCoverageChange?.(body.buckets.length > 0);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  function editBucket(bucketId, key, value) {
    setData((prev) => ({
      ...prev,
      buckets: prev.buckets.map((b) => (b.id === bucketId ? { ...b, [key]: value } : b)),
    }));
    setDirtyBucketIds((prev) => new Set(prev).add(bucketId));
  }

  function editItem(itemId, key, value) {
    setData((prev) => ({
      ...prev,
      buckets: prev.buckets.map((b) => ({
        ...b,
        items: b.items.map((it) => (it.id === itemId ? { ...it, [key]: value } : it)),
      })),
    }));
    setDirtyItemIds((prev) => new Set(prev).add(itemId));
  }

  // Add and delete reload every bucket from the server, which would discard unsaved edits in the
  // others, so save those first (same as Project Budgets' Add Line). `skip` is what's being deleted.
  async function flushDirty({ bucketId, itemIds = [] } = {}) {
    const items = data.buckets.flatMap((b) => b.items);
    const bucketIds = [...dirtyBucketIds].filter((id) => id !== bucketId);
    const dirtyItems = [...dirtyItemIds].filter((id) => !itemIds.includes(id));
    if (!bucketIds.length && !dirtyItems.length) return;
    await Promise.all([
      ...bucketIds.map((id) => apiFetch(`/billed-as-used/buckets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(bucketPayload(data.buckets.find((b) => b.id === id))),
      })),
      ...dirtyItems.map((id) => apiFetch(`/billed-as-used/items/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(itemPayload(items.find((it) => it.id === id))),
      })),
    ]);
    setDirtyBucketIds((prev) => { const next = new Set(prev); bucketIds.forEach((id) => next.delete(id)); return next; });
    setDirtyItemIds((prev) => { const next = new Set(prev); dirtyItems.forEach((id) => next.delete(id)); return next; });
  }

  async function addBucket() {
    setBusy('adding');
    setActionError('');
    try {
      await flushDirty();
      await apiFetch('/billed-as-used/buckets', {
        method: 'POST',
        body: JSON.stringify({ name: 'New budget', startingAmount: 0, projectId }),
      });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function addItem(bucketId) {
    setBusy('adding');
    setActionError('');
    try {
      await flushDirty();
      await apiFetch('/billed-as-used/items', { method: 'POST', body: JSON.stringify({ bucketId }) });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function saveBucket(bucket) {
    setBusy('saving');
    setActionError('');
    try {
      const itemIds = bucket.items.filter((it) => dirtyItemIds.has(it.id)).map((it) => it.id);
      await Promise.all([
        dirtyBucketIds.has(bucket.id)
          ? apiFetch(`/billed-as-used/buckets/${bucket.id}`, { method: 'PATCH', body: JSON.stringify(bucketPayload(bucket)) })
          : null,
        ...itemIds.map((id) => apiFetch(`/billed-as-used/items/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(itemPayload(bucket.items.find((it) => it.id === id))),
        })),
      ]);
      setDirtyBucketIds((prev) => { const next = new Set(prev); next.delete(bucket.id); return next; });
      setDirtyItemIds((prev) => { const next = new Set(prev); itemIds.forEach((id) => next.delete(id)); return next; });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function deleteBucket(bucket) {
    setActionError('');
    try {
      await flushDirty({ bucketId: bucket.id, itemIds: bucket.items.map((it) => it.id) });
      await apiFetch(`/billed-as-used/buckets/${bucket.id}`, { method: 'DELETE' });
      // Deletes cascade to the bucket's own lines, so any of those left dirty are just as moot as
      // the bucket itself -- left in place, a later Save would PATCH ids that no longer exist.
      const itemIds = new Set(bucket.items.map((it) => it.id));
      setDirtyBucketIds((prev) => { const next = new Set(prev); next.delete(bucket.id); return next; });
      setDirtyItemIds((prev) => {
        if (![...itemIds].some((id) => prev.has(id))) return prev;
        const next = new Set(prev);
        itemIds.forEach((id) => next.delete(id));
        return next;
      });
      load();
    } catch (e) {
      setActionError(e.message);
    }
  }

  async function deleteItem(id) {
    setActionError('');
    try {
      await flushDirty({ itemIds: [id] });
      await apiFetch(`/billed-as-used/items/${id}`, { method: 'DELETE' });
      setDirtyItemIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      load();
    } catch (e) {
      setActionError(e.message);
    }
  }

  const buckets = data?.buckets ?? [];

  return (
    <div>
      <ErrorBanner error={error} />
      {actionError && <ErrorBanner error={actionError} />}

      {/* Not gated on `!loading`: the refetch after every save would otherwise unmount the totals. */}
      {!!buckets.length && <BilledAsUsedTotals totals={data.totals} buckets={buckets} />}

      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button className="btn btn-outline" onClick={addBucket} disabled={!!busy}>
            {busy === 'adding' ? 'Adding…' : '+ Add budget'}
          </button>
        </div>
      )}

      {loading && !data ? (
        <div className="card" style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
      ) : !buckets.length ? (
        <div className="placeholder-page">
          <div className="placeholder-icon">💳</div>
          <div className="placeholder-title">No billed-as-used budget yet</div>
          <div className="placeholder-sub">
            {readOnly
              ? 'No billed-as-used budget has been set up for this project yet.'
              : 'Add the drawdown budget from the SOW above — its starting dollars and hours — then log what gets drawn against it.'}
          </div>
        </div>
      ) : (
        buckets.map((bucket) => (
          <BucketCard
            key={bucket.id}
            bucket={bucket}
            busy={busy}
            dirty={dirtyBucketIds.has(bucket.id) || bucket.items.some((it) => dirtyItemIds.has(it.id))}
            dirtyItemIds={dirtyItemIds}
            onEditBucket={(key, value) => editBucket(bucket.id, key, value)}
            onEditItem={editItem}
            onDeleteBucket={() => deleteBucket(bucket)}
            onAddItem={() => addItem(bucket.id)}
            onDeleteItem={deleteItem}
            onSave={() => saveBucket(bucket)}
            milestones={drawdownMilestones}
            portal={portal}
            readOnly={readOnly}
          />
        ))
      )}

      {/* Shown even with no buckets: the workbook's second sheet carries the commercial rules ("any
          leftover report hours can transfer to training"), which are worth recording before the
          first line is drawn. `portal` -- not `readOnly` -- gates this: the API's note routes carry
          no @AllowExternal at all (scope call, 2026-09-12), so a canEdit portal customer must still
          get the read-only rendering, not a form that would 403 on submit. See the class doc comment. */}
      {!loading && data && (
        portal
          ? <ReadOnlyGeneralNotes notes={data.notes} />
          : <GeneralNotes projectId={projectId} notes={data.notes} onChanged={load} />
      )}
    </div>
  );
}

/**
 * Exported for the customer view. Both "remaining" readings the sheet shows are here: what's left of
 * the hours sold, and what the remaining dollars buy at the agreed rate. They diverge as soon as a
 * line's dollars and hours aren't in step, and showing only one would hide that.
 */
export function BilledAsUsedTotals({ totals, buckets }) {
  const rated = buckets.filter((b) => b.totals.remainingHoursAtRate != null);
  const atRate = rated.length
    ? rated.reduce((sum, b) => sum + b.totals.remainingHoursAtRate, 0)
    : null;
  return (
    <div className="stat-row">
      <div className="stat">
        <div className="stat-value">{moneyWhole(totals.startingAmount)}</div>
        <div className="stat-label">Starting budget</div>
      </div>
      <div className="stat">
        <div className="stat-value">{moneyWhole(totals.amountUsed)}</div>
        <div className="stat-label">Drawn down</div>
      </div>
      <div className={`stat${totals.remainingAmount <= 0 ? ' is-alert' : ''}`}>
        <div className="stat-value">{moneyWhole(totals.remainingAmount)}</div>
        <div className="stat-label">Remaining</div>
      </div>
      <div className="stat">
        <div className="stat-value">
          {totals.remainingHours != null ? hours(totals.remainingHours) : hours(atRate)}
        </div>
        <div className="stat-label">
          {totals.remainingHours != null ? 'Hours remaining' : 'Hours remaining (at rate)'}
        </div>
      </div>
    </div>
  );
}

function BucketCard({
  bucket, busy, dirty, dirtyItemIds, onEditBucket, onEditItem, onDeleteBucket, onAddItem, onDeleteItem, onSave,
  milestones = [], portal = false, readOnly,
}) {
  const [confirmBucket, setConfirmBucket] = useState(false);

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="sheet-toolbar" style={{ marginBottom: 4 }}>
        {readOnly ? (
          <div className="card-title" style={{ marginBottom: 0 }}>{bucket.name}</div>
        ) : (
          <input
            className="cell-input" style={{ fontSize: 15, fontWeight: 700, maxWidth: 320 }}
            value={bucket.name} onChange={(e) => onEditBucket('name', e.target.value)}
          />
        )}
        <div className="spacer" />
        {!readOnly && (
          confirmBucket ? (
            <button className="icon-btn" onClick={() => { setConfirmBucket(false); onDeleteBucket(); }} disabled={!!busy} title="Confirm delete — removes its lines too">✓?</button>
          ) : (
            <button className="icon-btn" onClick={() => setConfirmBucket(true)} title="Delete this budget and its lines">✕</button>
          )
        )}
        {!readOnly && <button className="btn btn-outline" onClick={onAddItem} disabled={!!busy}>+ Add line</button>}
        {!readOnly && (
          <button className="btn btn-primary" onClick={onSave} disabled={!!busy || !dirty}>
            {busy === 'saving' ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </button>
        )}
      </div>

      {!readOnly && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 8 }}>
          <BucketField label="Starting budget ($)">
            <input type="number" step="0.01" min="0" className="cell-input" value={bucket.startingAmount ?? ''} onChange={(e) => onEditBucket('startingAmount', e.target.value)} />
          </BucketField>
          <BucketField label="Starting hours">
            <input type="number" step="0.01" min="0" className="cell-input" value={bucket.startingHours ?? ''} onChange={(e) => onEditBucket('startingHours', e.target.value)} />
          </BucketField>
          <BucketField label="Rate ($/hr)">
            <input type="number" step="0.01" min="0" className="cell-input" value={bucket.hourlyRate ?? ''} onChange={(e) => onEditBucket('hourlyRate', e.target.value)} />
          </BucketField>
          <BucketField label="Notes about this budget">
            <input className="cell-input" value={bucket.notes || ''} onChange={(e) => onEditBucket('notes', e.target.value)} />
          </BucketField>
        </div>
      )}

      {readOnly && (
        <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: -8, marginBottom: 4 }}>
          {money(bucket.startingAmount)}
          {bucket.startingHours != null && ` · ${hours(bucket.startingHours)} hours`}
          {bucket.hourlyRate != null && ` · ${money(bucket.hourlyRate)}/hr`}
        </div>
      )}

      {!portal && (
        <MilestoneLink bucket={bucket} milestones={milestones} readOnly={readOnly} onEditBucket={onEditBucket} />
      )}

      <BucketBalance bucket={bucket} />

      {!bucket.items.length ? (
        <div style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 12 }}>
          Nothing drawn against this budget yet.
        </div>
      ) : (
        <ItemsGrid items={bucket.items} dirtyItemIds={dirtyItemIds} onEdit={onEditItem} onDelete={onDeleteItem} readOnly={readOnly} />
      )}

      {readOnly && bucket.notes && (
        <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 12, whiteSpace: 'pre-wrap' }}>
          {bucket.notes}
        </div>
      )}
    </div>
  );
}

/**
 * Ties the pot to the contract line it draws against. Once linked, every drawn line is recognized on
 * the Milestone Tracker at its DAS submitted date instead of the milestone's single target date
 * (ADR 0046).
 *
 * The two amounts are shown side by side rather than reconciled: the milestone amount is what the
 * SOW contracted and the starting budget is what the register was set up with, and if they disagree
 * someone has to decide which is wrong -- quietly preferring one would hide it.
 */
function MilestoneLink({ bucket, milestones, readOnly, onEditBucket }) {
  // Resolved from the picker list first, so the amount note reflects an unsaved selection rather
  // than the last saved one. Falls back to the joined row, which is all the read-only view has.
  const linked = bucket.milestoneId
    ? milestones.find((m) => m.id === bucket.milestoneId) || bucket.milestone || null
    : null;
  const mismatch = linked && Number(linked.amount || 0) !== Number(bucket.startingAmount || 0);

  if (readOnly) {
    if (!linked) return null;
    return (
      <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 8 }}>
        Draws down milestone: <strong>{linked.title}</strong>
        {linked.dasNumber && ` (DAS ${linked.dasNumber})`}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 8 }}>
      <BucketField label="Draws down milestone">
        <select
          className="form-select cell-input"
          value={bucket.milestoneId || ''}
          onChange={(e) => onEditBucket('milestoneId', e.target.value || null)}
        >
          <option value="">Not tied to a milestone</option>
          {/* A milestone whose value type was changed away from Draw-down after linking would
              otherwise vanish from the list and read as "not tied to" anything. */}
          {linked && !milestones.some((m) => m.id === linked.id) && (
            <option value={linked.id}>{linked.title}</option>
          )}
          {milestones.map((m) => (
            <option key={m.id} value={m.id}>
              {m.title}{m.amount ? ` — ${money(m.amount)}` : ''}
            </option>
          ))}
        </select>
      </BucketField>
      {!milestones.length && !linked && (
        <div style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 2 }}>
          No Draw-down milestones on this project yet — set a milestone's Value to "Draw-down" on the
          project schedule first.
        </div>
      )}
      {mismatch && (
        <div style={{ fontSize: 11, color: 'var(--warn-text)', marginTop: 2 }}>
          Heads up: the milestone is {money(linked.amount)} and this budget starts at{' '}
          {money(bucket.startingAmount)}.
        </div>
      )}
    </div>
  );
}

function BucketField({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 2 }}>{label}</div>
      {children}
    </div>
  );
}

/** Exported for the customer view: the running balance is the point of the register. */
export function BucketBalance({ bucket }) {
  const t = bucket.totals;
  return (
    <table className="mini-table" style={{ marginTop: 12, maxWidth: 560 }}>
      <thead>
        <tr>
          <th />
          <th className="num">Budget</th>
          <th className="num">Hours</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Starting</td>
          <td className="num">{money(bucket.startingAmount)}</td>
          <td className="num">{hours(bucket.startingHours)}</td>
        </tr>
        <tr>
          <td>Drawn down</td>
          <td className="num">{money(t.amountUsed)}</td>
          <td className="num">{hours(t.hoursUsed)}</td>
        </tr>
        {/* Delivered but not yet invoiced -- what the PM works off at month end. Hidden when there's
            nothing outstanding rather than shown as a $0 row that means nothing. */}
        {!!t.unbilledAmount && (
          <tr>
            <td>Of which unbilled</td>
            <td className="num">{money(t.unbilledAmount)}</td>
            <td className="num">—</td>
          </tr>
        )}
      </tbody>
      <tfoot>
        <tr>
          <td>Remaining</td>
          <td className="num">{money(t.remainingAmount)}</td>
          <td className="num">
            {hours(t.remainingHours)}
            {/* Only when the two readings disagree: at the agreed rate the remaining dollars buy a
                different number of hours than the hours ledger says are left. */}
            {t.remainingHoursAtRate != null && t.remainingHoursAtRate !== t.remainingHours && (
              <span style={{ fontWeight: 400, color: 'var(--text-mid)' }}>
                {' '}({hours(t.remainingHoursAtRate)} at rate)
              </span>
            )}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

/** Exported: the customer portal renders the identical grid with `readOnly`. */
export function ItemsGrid({ items, dirtyItemIds, onEdit, onDelete, readOnly }) {
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState('');
  const [sort, toggleSort] = useGridSort('lineNumber');
  const sorted = useMemo(
    () => sortRows(items, sort, (row, key) => ITEM_SORT_VALUE[key](row)),
    [items, sort],
  );

  return (
    <div className="sheet-wrap" style={{ marginTop: 12 }}>
      <table className="sheet">
        <thead>
          <tr>
            <th style={{ width: 28 }} />
            <SortTh label="#" sortKey="lineNumber" sort={sort} onSort={toggleSort} style={{ width: 44 }} />
            <SortTh label="Item #" sortKey="itemNumber" sort={sort} onSort={toggleSort} style={{ width: 100 }} />
            <SortTh label="Description" sortKey="description" sort={sort} onSort={toggleSort} style={{ minWidth: 240 }} />
            <SortTh label="Budget used" sortKey="amountUsed" sort={sort} onSort={toggleSort} style={{ width: 120 }} />
            <SortTh label="Hours" sortKey="hoursUsed" sort={sort} onSort={toggleSort} style={{ width: 90 }} />
            <SortTh label="DAS #" sortKey="dasNumber" sort={sort} onSort={toggleSort} style={{ width: 100 }} />
            <SortTh label="DAS submitted" sortKey="dasSubmittedDate" sort={sort} onSort={toggleSort} style={{ width: 120 }} />
            <SortTh label="Billed" sortKey="billed" sort={sort} onSort={toggleSort} style={{ width: 70 }} />
            <th style={{ width: readOnly ? 12 : 36 }} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((item, i) => (
            <React.Fragment key={item.id}>
              <tr className={rowClassName(i, dirtyItemIds?.has(item.id))}>
                <td className="cell-check">
                  <button
                    onClick={() => setExpandedId((prev) => (prev === item.id ? null : item.id))}
                    title={expandedId === item.id ? 'Collapse' : 'Show detail'}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-mid)' }}
                  >
                    {expandedId === item.id ? '▾' : '▸'}
                  </button>
                </td>
                <td className="cell-num">{item.lineNumber}</td>
                {readOnly ? (
                  <td className="cell-read">{item.itemNumber || '—'}</td>
                ) : (
                  <td><input className="cell-input" value={item.itemNumber || ''} onChange={(e) => onEdit(item.id, 'itemNumber', e.target.value)} /></td>
                )}
                {readOnly ? (
                  <td className="cell-read" style={{ maxWidth: 360 }}>{item.description || '—'}</td>
                ) : (
                  <td><input className="cell-input" value={item.description || ''} onChange={(e) => onEdit(item.id, 'description', e.target.value)} /></td>
                )}
                {readOnly ? (
                  <td className="cell-num">{money(item.amountUsed)}</td>
                ) : (
                  <td><input type="number" step="0.01" className="cell-input" style={{ textAlign: 'right' }} value={item.amountUsed ?? ''} onChange={(e) => onEdit(item.id, 'amountUsed', e.target.value)} /></td>
                )}
                {readOnly ? (
                  <td className="cell-num">{hours(item.hoursUsed)}</td>
                ) : (
                  <td><input type="number" step="0.01" className="cell-input" style={{ textAlign: 'right' }} value={item.hoursUsed ?? ''} onChange={(e) => onEdit(item.id, 'hoursUsed', e.target.value)} /></td>
                )}
                {readOnly ? (
                  <td className="cell-read">{item.dasNumber || '—'}</td>
                ) : (
                  <td><input className="cell-input" value={item.dasNumber || ''} onChange={(e) => onEdit(item.id, 'dasNumber', e.target.value)} /></td>
                )}
                {readOnly ? (
                  <td className="cell-read">{displayDate(item.dasSubmittedDate)}</td>
                ) : (
                  <td><input type="date" className="cell-input" value={toDateInput(item.dasSubmittedDate)} onChange={(e) => onEdit(item.id, 'dasSubmittedDate', e.target.value)} /></td>
                )}
                <td className="cell-check">
                  {readOnly ? (
                    <span className={`badge ${item.billed ? 'badge-green' : 'badge-gray'}`}>{item.billed ? 'Yes' : 'No'}</span>
                  ) : (
                    <input type="checkbox" checked={!!item.billed} onChange={(e) => onEdit(item.id, 'billed', e.target.checked)} />
                  )}
                </td>
                <td className="cell-check">
                  {!readOnly && (
                    confirmId === item.id ? (
                      <button className="icon-btn" onClick={() => { setConfirmId(''); onDelete(item.id); }} title="Confirm delete — can't be undone">✓?</button>
                    ) : (
                      <button className="icon-btn" onClick={() => setConfirmId(item.id)} title="Delete this line">✕</button>
                    )
                  )}
                </td>
              </tr>
              {expandedId === item.id && (
                <tr>
                  <td colSpan={10} style={{ background: 'var(--light-bg)' }}>
                    <div style={{ padding: '12px 8px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 12 }}>
                        <DetailField label="Requested">
                          {readOnly ? displayDate(item.requestedDate) : (
                            <input type="date" className="cell-input" style={{ padding: '4px 0' }} value={toDateInput(item.requestedDate)} onChange={(e) => onEdit(item.id, 'requestedDate', e.target.value)} />
                          )}
                        </DetailField>
                        <DetailField label="Approved">
                          {readOnly ? displayDate(item.approvedDate) : (
                            <input type="date" className="cell-input" style={{ padding: '4px 0' }} value={toDateInput(item.approvedDate)} onChange={(e) => onEdit(item.id, 'approvedDate', e.target.value)} />
                          )}
                        </DetailField>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)', marginBottom: 4 }}>Notes</div>
                      {readOnly ? (
                        <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{item.notes || '—'}</div>
                      ) : (
                        <AutoGrowTextarea style={{ background: 'var(--surface)', borderRadius: 'var(--radius)' }} value={item.notes || ''} onChange={(e) => onEdit(item.id, 'notes', e.target.value)} />
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetailField({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{children}</div>
    </div>
  );
}

/**
 * The workbook's second sheet: numbered general notes about the log as a whole, not about one
 * bucket. Clark's are specifically about moving hours *between* buckets, which is why they're
 * project-scoped. Unchanged by the 2026-09-12 inline-editing rework: it already saves each note
 * immediately, the same convention RAAIDD's review-update history uses.
 */
/** The portal's read-only rendering of the same notes -- no add/edit/delete affordances at all. */
function ReadOnlyGeneralNotes({ notes }) {
  if (!notes.length) return null;
  return (
    <div className="card">
      <div className="card-title">General notes</div>
      <ol style={{ margin: 0, paddingLeft: 22, fontSize: 13 }}>
        {notes.map((n) => (
          <li key={n.id} style={{ marginBottom: 6, whiteSpace: 'pre-wrap' }}>{n.text}</li>
        ))}
      </ol>
    </div>
  );
}

function GeneralNotes({ projectId, notes, onChanged }) {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null); // { id, text } | null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function call(path, method, body) {
    setBusy(true);
    setError('');
    try {
      await apiFetch(path, { method, ...(body ? { body: JSON.stringify(body) } : {}) });
      onChanged();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-title">General notes</div>
      <ErrorBanner error={error} />
      {!notes.length && !draft && (
        <div style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 12 }}>
          The commercial rules for this budget — how hours can move between buckets, when they
          expire — so they don't only live in someone's inbox.
        </div>
      )}
      <ol style={{ margin: '0 0 12px', paddingLeft: 22, fontSize: 13 }}>
        {notes.map((note) => (
          <li key={note.id} style={{ marginBottom: 8 }}>
            {editing?.id === note.id ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <textarea
                  className="form-input"
                  rows={2}
                  value={editing.text}
                  onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                />
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 11, padding: '3px 8px' }}
                  disabled={busy || !editing.text.trim()}
                  onClick={async () => {
                    if (await call(`/billed-as-used/notes/${note.id}`, 'PATCH', { text: editing.text.trim() })) {
                      setEditing(null);
                    }
                  }}
                >
                  Save
                </button>
                <button className="btn btn-outline" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setEditing(null)}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{note.text}</span>
                <button className="btn btn-outline" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setEditing({ id: note.id, text: note.text })}>Edit</button>
                <button
                  className="btn btn-outline"
                  style={{ fontSize: 11, padding: '3px 8px' }}
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm('Delete this note?')) call(`/billed-as-used/notes/${note.id}`, 'DELETE');
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </li>
        ))}
      </ol>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <textarea
          className="form-input"
          rows={2}
          placeholder="Add a note…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button
          className="btn btn-outline"
          disabled={busy || !draft.trim()}
          onClick={async () => {
            if (await call('/billed-as-used/notes', 'POST', { projectId, text: draft.trim() })) {
              setDraft('');
            }
          }}
        >
          Add note
        </button>
      </div>
    </div>
  );
}
